import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { getLoggedUserInfos, logout as logoutAPI } from './requests'

import { useNavigate } from 'react-router-dom'
import { AuthModel } from '../../models/AuthModel'
import { Employee } from '../../models/Employee'
import * as authHelper from '../AuthLocalStorageHelper'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: Employee | undefined
  setCurrentUser: Dispatch<SetStateAction<Employee | undefined>>
  isLogged: boolean | undefined
  setIsLogged: Dispatch<SetStateAction<boolean | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  isLogged: undefined,
  setIsLogged: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()

  const [currentUser, setCurrentUser] = useState<Employee | undefined>()
  const [isLogged, setIsLogged] = useState<boolean | undefined>(undefined)
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    // Whatever the request result
    // The user must be logged out
    logoutAPI().finally(() => {
      setIsLogged(false)
      setCurrentUser(undefined)
      saveAuth(undefined)
      navigate('/auth/login')
    })
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        isLogged,
        setIsLogged,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<PropsWithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser, setIsLogged } = useAuth()
  const didRequest = useRef(false)

  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const { data } = await getLoggedUserInfos()
          if (data) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        if (!didRequest.current) {
          logout()
        }
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.access_token) {
      setIsLogged(true)
      requestUser()
    } else {
      setIsLogged(false)
    }
  }, [])

  return <>{children}</>
}

export { AuthInit, AuthProvider, useAuth }
